import html2canvas from 'html2canvas';

/**
 * Capture d'un élément de la page pour un signalement.
 *
 * Deux écueils de html2canvas sont contournés ici :
 *
 * - Lenteur. Il clone tout le document, et pour chaque nœud situé dans un élément personnalisé
 *   — c'est-à-dire, sous Angular, toute la page puisqu'elle vit dans app-root — il recopie les
 *   quelque 350 propriétés calculées dans le style en ligne. Le coût suivait donc la taille de
 *   la page, pas celle de l'élément, et le thread principal restait bloqué pendant que la
 *   popup était affichée. On ne clone plus que la chaîne des ancêtres et le sous-arbre visé.
 *
 * - Canvas WebGL vides. Sans preserveDrawingBuffer, le navigateur efface le tampon dès l'image
 *   présentée : la vue 3D et les cartes Mapbox sortaient noires. Voir enableWebglReadback.
 */

/**
 * Côté le plus long de la capture : au-delà, un élément pleine page produirait une image énorme.
 * Le serveur refuse toute image plus grande (ScreenshotFormat.MaxSide).
 */
const MAX_CAPTURE_SIDE = 2400;
/** Attente maximale d'une image du sous-arbre ; 15 s par défaut dans html2canvas. */
const IMAGE_TIMEOUT_MS = 3000;
/** Taille maximale d'une capture, celle qu'accepte le serveur (BugReportsController). */
const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
/** Réductions successives tentées en JPEG quand la capture dépasse la limite. */
const SHRINK_FACTOR = 0.7;
const SHRINK_ATTEMPTS = 4;

const WEBGL_CONTEXTS = new Set(['webgl', 'webgl2', 'experimental-webgl']);
/** Toujours clonés : ils portent les feuilles de style dont le rendu dépend. */
const ALWAYS_KEPT_TAGS = new Set(['HEAD', 'STYLE', 'LINK', 'BASE', 'META', 'TITLE']);

let webglReadbackEnabled = false;

/**
 * Force preserveDrawingBuffer sur les contextes WebGL créés à partir de maintenant, pour que
 * leur contenu reste lisible entre deux images — quelle que soit la bibliothèque qui les crée
 * (three.js, Mapbox…), sans toucher à chacune.
 *
 * Le tampon conservé coûte une copie par image : on ne l'active que pour les utilisateurs qui
 * peuvent signaler. Il doit l'être avant la création des vues, d'où un appel dès la
 * construction du bouton, monté à la racine de l'application. Le régler vue par vue, à la
 * création de chaque contexte, l'imposerait à tous les utilisateurs de ces vues — or un
 * signalement peut viser n'importe quelle vue, à tout moment.
 */
export function enableWebglReadback(): void {
  if (webglReadbackEnabled) {
    return;
  }
  webglReadbackEnabled = true;

  const original = HTMLCanvasElement.prototype.getContext;
  HTMLCanvasElement.prototype.getContext = function (
    this: HTMLCanvasElement,
    contextId: string,
    options?: Record<string, unknown>,
  ) {
    const attributes = WEBGL_CONTEXTS.has(contextId)
      ? { ...(options ?? {}), preserveDrawingBuffer: true }
      : options;
    return original.call(this, contextId, attributes);
  } as typeof original;
}

/**
 * Faut-il cloner ce nœud pour capturer la cible ? Oui pour ses ancêtres, son sous-arbre et ce
 * qui porte des styles ; non pour tout le reste de la page.
 *
 * Retirer les voisins ne déforme pas la cible : html2canvas fige dans chaque clone ses styles
 * calculés, largeurs comprises, et ne rend que la cible, quelle que soit sa nouvelle position.
 */
function keepForCapture(node: Element, target: Element): boolean {
  if (node.contains(target) || target.contains(node)) {
    return true;
  }
  if (ALWAYS_KEPT_TAGS.has(node.tagName) || node.closest('head')) {
    return true;
  }
  // Sprites d'icônes : un <use href="#icone"> de la cible pointe vers un <symbol> ailleurs.
  return node.tagName.toLowerCase() === 'svg' && node.querySelector('symbol, defs') !== null;
}

/**
 * Capture de l'élément — PNG, ou JPEG si elle dépasse la limite du serveur (voir fitWithinLimit) —
 * ou null si le navigateur la refuse (image tierce, élément détaché…).
 */
export async function captureElement(element: Element): Promise<Blob | null> {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return null;
  }

  const longestSide = Math.max(rect.width, rect.height);
  const scale = Math.min(window.devicePixelRatio || 1, MAX_CAPTURE_SIDE / longestSide);

  const canvas = await html2canvas(element as HTMLElement, {
    useCORS: true,
    logging: false,
    scale,
    imageTimeout: IMAGE_TIMEOUT_MS,
    ignoreElements: node => node.tagName.toLowerCase() === 'app-bug-report' || !keepForCapture(node, element),
  });

  return fitWithinLimit(canvas);
}

/**
 * Capture sous la limite du serveur. Le PNG d'un élément très détaillé (photo satellite) dépasse
 * vite 10 Mo : on passe alors au JPEG, puis on réduit l'image jusqu'à ce qu'elle tienne. Null si
 * elle ne tient toujours pas : le ticket part sans image plutôt que d'être refusé en entier.
 */
async function fitWithinLimit(capture: HTMLCanvasElement): Promise<Blob | null> {
  // L'échelle de html2canvas arrondit : la capture peut dépasser le côté maximal d'un pixel.
  const longestSide = Math.max(capture.width, capture.height);
  const canvas = longestSide > MAX_CAPTURE_SIDE ? resize(capture, MAX_CAPTURE_SIDE / longestSide) : capture;

  const png = await toBlob(canvas, 'image/png');
  if (png && png.size <= MAX_SCREENSHOT_BYTES) {
    return png;
  }

  let factor = 1;
  for (let attempt = 0; attempt < SHRINK_ATTEMPTS; attempt++) {
    const jpeg = await toBlob(flatten(canvas, factor), 'image/jpeg', 0.85);
    if (jpeg && jpeg.size <= MAX_SCREENSHOT_BYTES) {
      return jpeg;
    }
    factor *= SHRINK_FACTOR;
  }
  return null;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

/** Copie réduite de la capture sur fond blanc : le JPEG n'a pas de transparence, qui sortirait noire. */
function flatten(canvas: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  return resize(canvas, factor, '#fff');
}

/** Copie de la capture à l'échelle `factor` (arrondie vers le bas, sans dépasser), sur `background` s'il est donné. */
function resize(canvas: HTMLCanvasElement, factor: number, background?: string): HTMLCanvasElement {
  const copy = document.createElement('canvas');
  copy.width = Math.max(1, Math.floor(canvas.width * factor));
  copy.height = Math.max(1, Math.floor(canvas.height * factor));
  const context = copy.getContext('2d');
  if (context) {
    if (background) {
      context.fillStyle = background;
      context.fillRect(0, 0, copy.width, copy.height);
    }
    context.drawImage(canvas, 0, 0, copy.width, copy.height);
  }
  return copy;
}
